import { useTranslation } from "react-i18next";
import { ExternalLink, PauseCircle, PlayCircle } from "lucide-react";
import Img from "../components/ui/Img";
import { shownImage, useExternalMediaAllowed } from "../lib/externalMedia";
import { formatDuration } from "./formatDuration";
import { kindIcon } from "./icons";
import { kindOf } from "./registry";
import type { PlayableItem } from "./types";

/** What the row reads of a wall item (or any other playable item). */
export type PlayableItemLike = PlayableItem & {
  id: string;
  thumbnailUrl?: string | null;
  durationMs: number | null;
};

export interface PlayableItemRowProps {
  item: PlayableItemLike;
  /** This item is the one in the dock. */
  playing: boolean;
  /** The dock is paused (only meaningful when `playing`). */
  paused: boolean;
  onPress(): void;
  /** A line under the row; none when unset. */
  caption?: string | null;
}

/**
 * One playable row: thumbnail (or kind glyph), title, meta line and a play / pause button. A format this browser cannot
 * decode is dimmed and its link opens in a new tab (Figma Shared / Wall / Album item row State=Can't play here
 * 4354:29401). Plain props only: the caller owns the playback state and what pressing does.
 */
export default function PlayableItemRow({ item: it, playing, paused, onPress, caption }: PlayableItemRowProps) {
  const { t } = useTranslation();
  const externalOk = useExternalMediaAllowed();
  const kind = kindOf(it);
  const sounding = playing && !paused;
  const cantPlay = !kind.canPlay(it);
  const openUrl = cantPlay ? kind.playUrl(it) : null;
  const meta = cantPlay ? t("wall_item_cant_play", "Can't play here · open in a new tab") : [kind.meta(t), formatDuration(it.durationMs)].filter(Boolean).join(" · ");
  const Glyph = kindIcon(kind);
  const label = sounding ? t("wall_pause") : t("wall_play");
  return (
    <li data-testid="wall-item-row" data-cant-play={cantPlay ? "true" : undefined} className="flex flex-col">
      <div className="flex items-center gap-3 px-4 py-1">
        <div className={`flex flex-1 min-w-0 items-center gap-3 ${cantPlay ? "opacity-60" : ""}`}>
          {shownImage(it.thumbnailUrl, externalOk) ? (
            <Img src={it.thumbnailUrl as string} alt="" className="w-16 h-9 rounded object-cover shrink-0" />
          ) : (
            <div className="w-16 h-9 rounded bg-[var(--bg-elevated)] text-[var(--foreground-muted)] flex items-center justify-center shrink-0">
              <Glyph size={20} aria-hidden="true" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium leading-none text-[var(--foreground)] truncate">{it.title ?? it.url}</div>
            <div className={`text-xs leading-[1.3] ${playing ? "text-[var(--primary)] dark:text-[var(--primary-on-surface)]" : kind.caps.live && !cantPlay ? "text-[#4cb0a6]" : "text-[var(--foreground-muted)]"}`}>
              {playing ? `${t("cast_in_dock", "Playing in the dock")} · ${meta}` : meta}
            </div>
          </div>
        </div>
        {cantPlay ? (
          openUrl && (
            <a
              href={openUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="wall-item-open-new-tab"
              aria-label={t("player_open_in_new_tab", "Open in a new tab")}
              title={t("player_open_in_new_tab", "Open in a new tab")}
              className="shrink-0 text-[var(--foreground-muted)]"
            >
              <ExternalLink size={24} aria-hidden="true" />
            </a>
          )
        ) : (
          <button
            type="button"
            aria-label={label}
            className={`shrink-0 ${playing ? "text-[var(--primary)] dark:text-[var(--primary-on-surface)]" : "text-[var(--foreground-muted)]"}`}
            onClick={onPress}
          >
            {sounding ? <PauseCircle size={24} aria-hidden="true" /> : <PlayCircle size={24} aria-hidden="true" />}
          </button>
        )}
      </div>
      {caption ? <p data-testid="playable-item-caption" className="m-0 px-4 pb-1 text-xs text-[var(--foreground-muted)]">{caption}</p> : null}
    </li>
  );
}
